import type { IconName } from '@/components/kit';

/**
 * THE THIRTEEN DOORS of System Settings — `g_set.dc.html`, in the board's own
 * order, with the board's own titles, sub-lines and icons (each icon was
 * matched against the artboard's SVG geometry, not guessed by name).
 *
 * `to` is where the door leads — every one of the thirteen has a screen.
 * Directory leads to the Directory surface rather than a second copy of it;
 * Flows leads to Settings ▸ Flows, which mounts the Library's own editor.
 *
 * `live` marks the four doors whose sub-line is a live figure rather than the
 * board's static phrase — those read their own number, and say so honestly when
 * the role is not allowed to see it.
 */

export type SettingsDoorKey =
  | 'facility' | 'branding' | 'domains' | 'recording' | 'ids' | 'privacy'
  | 'roles' | 'money' | 'subscription' | 'modules' | 'wallet' | 'directory'
  | 'flows';

export interface SettingsDoor {
  key: SettingsDoorKey;
  title: string;
  /** The board's static sub-line, used when the door has no live figure. */
  sub: string;
  icon: IconName;
  /** Where it leads. */
  to: string;
  /** This door's sub-line is a figure, computed by the grid. */
  live?: 'subscription' | 'wallet' | 'directory' | 'flows';
}

export const SETTINGS_DOORS: SettingsDoor[] = [
  {
    key: 'facility', title: 'Facility', icon: 'building-hospital',
    sub: 'contact · license · language · currency',
    to: '/clinic-manager/settings/facility',
  },
  {
    key: 'branding', title: 'Branding', icon: 'palette',
    sub: 'identity · theme · login · documents',
    to: '/clinic-manager/settings/branding',
  },
  {
    key: 'domains', title: 'Domains', icon: 'world',
    sub: 'clinic + patient-portal addresses', to: '/clinic-manager/settings/domains',
  },
  {
    key: 'recording', title: 'Recording', icon: 'clock',
    sub: 'session limits', to: '/clinic-manager/settings/recording',
  },
  {
    key: 'ids', title: 'Patient IDs', icon: 'hash',
    sub: 'MRN format', to: '/clinic-manager/settings/patient-ids',
  },
  {
    key: 'privacy', title: 'Privacy', icon: 'shield-check',
    sub: 'audio retention · export everything', to: '/clinic-manager/settings/privacy',
  },
  {
    key: 'roles', title: 'Roles & access', icon: 'users',
    sub: 'people, roles, grades, coverage', to: '/clinic-manager/settings/roles',
  },
  {
    key: 'money', title: 'Money', icon: 'cash',
    sub: 'tax · currency format · shares · discount policy', to: '/clinic-manager/settings/money',
  },
  {
    key: 'subscription', title: 'Subscription', icon: 'receipt-2',
    sub: 'plan · next invoice', to: '/clinic-manager/settings/subscription', live: 'subscription',
  },
  {
    key: 'modules', title: 'Modules', icon: 'layout-grid',
    sub: 'turn off what you do not use', to: '/clinic-manager/settings/modules',
  },
  {
    key: 'wallet', title: 'Message wallet', icon: 'wallet',
    sub: 'balance · runway', to: '/clinic-manager/settings/wallet', live: 'wallet',
  },
  /* THE REGISTRY IS ITS OWN SURFACE. Settings ▸ Directory is the same screen
     the Directory rail door opens, so this leads there rather than to a second
     copy of it under /clinic-manager/. */
  {
    key: 'directory', title: 'Directory', icon: 'address-book',
    sub: 'external providers', to: '/directory', live: 'directory',
  },
  {
    key: 'flows', title: 'Flows', icon: 'route',
    sub: 'the shared editor', to: '/clinic-manager/settings/flows', live: 'flows',
  },
];
